import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import Chip from '@mui/material/Chip'
import { alpha, useTheme } from '@mui/material/styles'
import React from 'react'

import { CHIP_TINT_OPACITY, NOTIFICATION_CHIP_DEFAULTS } from './notificationCenterConstants'
import { areaFor } from './notificationCenterViews'

import type { Notification } from 'types/modules/notification-center/notification'

type AreaChipMeta = {
  Icon: typeof AltRouteOutlinedIcon
  palette: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
}

/**
 * Icon per CRM area, echoing the app bar's module glyphs. MUI icons rather than
 * the app bar's own `MEDIA_SERVER` SVGs because those are `<img>` and can't take
 * a theme color — the chips need to be tintable.
 */
const AREA_CHIP_META: Readonly<Record<string, AreaChipMeta>> = Object.freeze({
  Customers: { Icon: PeopleOutlineOutlinedIcon, palette: 'info' },
  Billing: { Icon: ReceiptLongOutlinedIcon, palette: 'primary' },
  Payments: { Icon: PaymentsOutlinedIcon, palette: 'success' },
  Reporting: { Icon: AssessmentOutlinedIcon, palette: 'secondary' },
  Routing: { Icon: AltRouteOutlinedIcon, palette: 'warning' },
  Exceptions: { Icon: ReportProblemOutlinedIcon, palette: 'error' },
})

type NotificationTypeChipProps = {
  notification: Notification
  /** Both default from `NOTIFICATION_CHIP_DEFAULTS`, so icons or color can be dropped globally. */
  showIcon?: boolean
  showColor?: boolean
}

const NotificationTypeChip = ({
  notification,
  showIcon = NOTIFICATION_CHIP_DEFAULTS.showIcon,
  showColor = NOTIFICATION_CHIP_DEFAULTS.showColor,
}: NotificationTypeChipProps) => {
  const theme = useTheme()
  const area = areaFor(notification)
  const meta = AREA_CHIP_META[area]
  const color = showColor && meta ? theme.palette[meta.palette].main : undefined

  return (
    <Chip
      icon={showIcon && meta ? <meta.Icon fontSize="small" /> : undefined}
      label={area}
      size="small"
      sx={
        color
          ? {
              color,
              borderColor: alpha(color, CHIP_TINT_OPACITY.border),
              bgcolor: alpha(color, CHIP_TINT_OPACITY.background),
              // Chip's own `.MuiChip-icon` rule beats a plain nested selector, so bump specificity.
              '&& .MuiChip-icon': { color },
            }
          : undefined
      }
      variant="outlined"
    />
  )
}

export default NotificationTypeChip
